const validTransitionTypes = [
  'slide',
  'fade'
];

class Carousel extends HTMLElement {
  constructor() {
    super();
    this.itemsHolder = null;
  }

  connectedCallback() {
    this.itemsHolder = this.querySelector('carousel-block-items');
    if(!this.itemsHolder) {
      throw new Error('Carousel requires a carousel-block-items element.');
    }

    this.autoSlide = (this.hasAttribute('auto-slide') && this.getAttribute('auto-slide') !== 'false');
    this.slideSpeed = parseInt(this.getAttribute('slide-speed') ?? 10);
    this.transitionType = this.getAttribute('transition-type') ?? 'slide';
    this.style.opacity = 1;
    this.style.height = 'auto';
  }

  get autoSlide() {
    return this._autoSlide;
  }

  set autoSlide(value) {
    this._autoSlide = value;
  }

  get slideSpeed() {
    return this._slideSpeed;
  }

  set slideSpeed(value) {
    this._slideSpeed = parseInt(value);
  }

  get transitionType() {
    return this._transitionType;
  }

  set transitionType(value) {
    if(!validTransitionTypes.includes(value)) {
      console.error('Carousel transition type is invalid. Defaulting to slide.');
      value = 'slide';
    }
    this._transitionType = value;
  }

  nextItem() {
    this.itemsHolder.nextItem();
  }

  prevItem() {
    this.itemsHolder.prevItem();
  }

  reload() {
    this.itemsHolder.reload();
  }

  hideNav() {
    const prev = this.querySelector('carousel-prev-arrow');
    const next = this.querySelector('carousel-next-arrow');
    if (prev) prev.classList.add('hidden');
    if (next) next.classList.add('hidden');
  }

  showNav() {
    const prev = this.querySelector('carousel-prev-arrow');
    const next = this.querySelector('carousel-next-arrow');
    if (prev) prev.classList.remove('hidden');
    if (next) next.classList.remove('hidden');
  }
}

class CarouselItems extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.items = [];
    this.initialItems = [];
  }

  connectedCallback() {
    this.carousel = this.closest('carousel-block');
    if(!this.carousel) {
      throw new Error('No parent carousel-block element found.');
    }
    this.reload();
    window.addEventListener('resize', () => {
      this.setColumnDimensions();
    });
  }

  getCurrentViewport(width) {
    // Default to mobile.
    let value = 'mobile';
    if(width >= this.largeBreakpoint) {
      value = 'desktop';
    }
    else if(width >= this.smallBreakpoint) {
      value = 'tablet';
    }
    return value;
  }

  get viewport() {
    return this._viewport;
  }

  set viewport(value) {
    this._viewport = value ?? 'mobile';
  }

  get smallBreakpoint() {
    return this._smallBreakpoint ?? 768;
  }

  set smallBreakpoint(value) {
    this._smallBreakpoint = parseInt(value);
  }

  get largeBreakpoint() {
    return this._largeBreakpoint ?? 1280;
  }

  set largeBreakpoint(value) {
    this._largeBreakpoint = parseInt(value);
  }

  get minHeight() {
    return this._minHeight ?? 0;
  }

  set minHeight(value) {
    this._minHeight = parseFloat(value);
    this.style.minHeight = `${this._minHeight}px`;
  }

  get columnGap() {
    return this._columnGap ?? 0;
  }

  set columnGap(value) {
    this._columnGap = parseFloat(value);
  }

  get columnWidth() {
    return this._columnWidth ?? 0;
  }

  set columnWidth(value) {
    this._columnWidth = parseFloat(value);
  }

  getItems() {
    const items = this.querySelectorAll('carousel-block-item');
    this.initialItems = [...items];
    this.items = [...items];
  }

  reload() {
    this.getItems();
    this.setColumnDimensions();
  }

  setColumnDimensions() {
    this.clearAuto();
    const style = getComputedStyle(this);
    this.columnGap = style.columnGap;
    const width = parseFloat(style.width) + this.columnGap;
    this.viewport = this.getCurrentViewport(width);
    this.toggleNav();
    switch (this.viewport) {
      case 'mobile':
        this.columnWidth = (width / this.mobileColumns) - this.columnGap
        break;
      case 'tablet':
        this.columnWidth = (width / this.tabletColumns) - this.columnGap;
        break;
      case 'desktop':
        this.columnWidth = (width / this.desktopColumns) - this.columnGap;
        break;
    }
    const heights = [];
    this.items.forEach((i,k) => {
      i.style.minWidth = `${this.columnWidth}px`;
      i.style.width = `${this.columnWidth}px`;
      heights.push(this.calcHeight(i));
      i.style.translate = `${k * (this.columnWidth + this.columnGap)}px`;
    });
    const extraHeight = parseFloat(style.paddingBottom) + parseFloat(style.paddingTop) + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    this.minHeight = Math.max(...heights) + extraHeight;
    this.resetAuto();
  }

  calcHeight(element) {
    const clone = element.cloneNode(true);
    Object.assign(clone.style, {
      overflow: 'visible',
      height: 'auto',
      maxHeight: 'none',
      opacity: '0',
      visibility: 'hidden',
      display: 'block'
    });
    this.after(clone);
    const height = clone.offsetHeight;
    clone.remove();
    return height;
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  isVisible(element) {
    const rect = element.getBoundingClientRect();
    const viewport = this.getBoundingClientRect();
    return rect.right >= viewport.left && rect.right <= viewport.right; 
  }

  get desktopColumns() {
    return parseInt(this.getAttribute('desktop-columns') ?? 4);
  }

  set desktopColumns(columns) {
    this.setAttribute('desktop-columns', columns);
  }

  get tabletColumns() {
    return parseInt(this.getAttribute('tablet-columns') ?? 4);
  }

  set tabletColumns(columns) {
    this.setAttribute('tablet-columns', columns);
  }

  get mobileColumns() {
    return parseInt(this.getAttribute('mobile-columns') ?? 2);
  }

  set mobileColumns(columns) {
    this.setAttribute('mobile-columns', columns);
  }

  get numColumns() {
    switch (this.viewport) {
      case 'mobile':
        return this.mobileColumns
      case 'tablet':
        return this.tabletColumns
      case 'desktop':
        return this.desktopColumns
    }
  }

  setAuto() {
    if (!Shopify.designMode && this.carousel.autoSlide && this.numColumns < this.items.length) {
      this.auto = setTimeout(() => {
        this.nextItem()
      }, this.carousel.slideSpeed * 1000);
    }
  }

  resetAuto() {
    if(!this.isInViewport(this)) {
      const observer = new IntersectionObserver(function(entries) {
        if(entries[0].isIntersecting === true) {
          entries[0]['target'].clearAuto();
          entries[0]['target'].setAuto();
        }
      }, { threshold: [0]});
      observer.observe(this);
    }
    else {
      if (!Shopify.designMode && this.carousel.autoSlide) {
        this.clearAuto();
        this.setAuto();
      }
    }
  }

  clearAuto() {
    if (!Shopify.designMode && this.carousel.autoSlide) {
      clearTimeout(this.auto);
    }
  }

  toggleNav() {
    this.items.length <= this.numColumns ? this.carousel.hideNav() : this.carousel.showNav();
  }

  shiftFirstItem() {
    const items = [...this.items];
    const firstItem = items.shift();
    items.push(firstItem);
    firstItem.disableTransition();
    firstItem.style.translate = `${(items.length - 1) * (this.columnWidth + this.columnGap)}px`;
    firstItem.enableTransition();
    this.items = items.filter(i => i);
  }

  shiftLastItem() {
    const items = [...this.items];
    const lastItem = items.pop();
    const firstItem = items[0];
    items.unshift(lastItem);
    lastItem.disableTransition();
    const position = 0 - (this.columnWidth + this.columnGap);
    if(items.filter(i => parseFloat(i.style.translate).toFixed(2) === position.toFixed(2)).length > 0) {
      lastItem.style.translate = `${position * 2}px`;
    }
    else {
      lastItem.style.translate = `${position}px`;
    }
    lastItem.enableTransition();
    this.items = items.filter(i => i);
  }

  nextItem() {
    this.clearAuto();
    if(this.numColumns >= this.items.length) {
      return false;
    }
    if(parseFloat(this.items[0].style.translate) < 0) {
      this.shiftFirstItem();
    }
    const items = [...this.items]
    items.forEach((i,k) => {
      const currPos = parseFloat(i.style.translate)
      i.style.translate = `${currPos - (this.columnWidth + this.columnGap)}px`;
    })
    this.resetAuto();
  }

  prevItem() {
    this.clearAuto();
    if(this.numColumns >= this.items.length) {
      return false;
    }
    if(!this.isVisible(this.items[this.items.length - 1])) {
      this.shiftLastItem();
    }
    const items = [...this.items]
    items.forEach((i,k) => {
      const currPos = parseFloat(i.style.translate)
      i.style.translate = `${currPos + (this.columnWidth + this.columnGap)}px`;
    })
    this.resetAuto();
  }

  jumpTo(item) {
    const before = [];
    const after = [];
    let found = false;
    let items = [...this.items];
    if(!this.isVisible(items[0])) {
      this.shiftFirstItem();
    }
    if(!this.isVisible(items[items.length - 1])) {
      this.shiftLastItem();
    }
    this.items.forEach((i,k) => {
      i.disableTransition();
      if(i === item) {
        found = true;
      }
      found ? before.push(i) : after.push(i);
    })
    this.items = [...before, ...after];
    this.setColumnDimensions();
    this.enableTransitions();
  }

  disableTransitions() {
    this.items.forEach(i => {
      i.disableTransition();
    })
  }

  enableTransitions() {
    this.items.forEach(i => {
      i.enableTransition();
    })
  }
}

class CarouselItem extends HTMLElement {
  constructor() {
    super();
    this.holder = null;
    this.imagesToLoad = [];
  }

  connectedCallback() {
    this.holder = this.closest('carousel-block-items');
    this.handleLazyLoading()
    this.screenX = 0;
    this.enableTransition();
    this.setupEvents();
  }

  handleLazyLoading() {
    const images = this.querySelectorAll('img');
    if(images.length > 0) {
      images.forEach(i => {
        if(!i.complete) {
          i.addEventListener('load', this.handleImageLoad.bind(this));
          i.addEventListener('error', this.handleImageLoad.bind(this));
          this.imagesToLoad.push(i);
        }
      })
    }
  }

  handleImageLoad() {
    const reloadedImages = [];
    this.imagesToLoad.forEach(i => {
      if(i.complete) {
        i.removeEventListener('load', this.handleImageLoad.bind(this));
        this.holder.setColumnDimensions();
      }
      else {
        reloadedImages.push(i)
      }
    });
    this.imagesToLoad = reloadedImages;
  }

  disableTransition() {
    this.style.transition = 'none';
  }

  enableTransition () {
    this.flushCss();
    this.style.transition = 'translate 500ms';
  }

  flushCss() {
    this.offsetHeight;
  }

  setActive() {
    this.holder.jumpTo(this);
  }

  setupEvents() {
    this.addEventListener('touchstart', this.setPageX.bind(this))
    this.addEventListener('touchend', this.checkDragged.bind(this))
  }

  setPageX(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    this.screenX = touch.screenX;
  }

  checkDragged(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    if(touch.screenX < this.screenX) {
      if((this.screenX - touch.screenX) > 40) {
        this.holder.nextItem();
      }
    }
    else if(touch.screenX > this.screenX) {
      if((touch.screenX - this.screenX) > 40) {
        this.holder.prevItem();
      }
    }
  }
  
}

class CarouselNav extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.closest('carousel-block');
  }

  prevItem() {
    this.carousel.prevItem();
  }

  nextItem() {
    this.carousel.nextItem();
  }
}

class CarouselPrev extends CarouselNav {
  constructor() {
    super();
    this.addEventListener('click', this.prevItem.bind(this));
  }
}

class CarouselNext extends CarouselNav {
  constructor() {
    super();
    this.addEventListener('click', this.nextItem.bind(this));
  }
}

customElements.define('carousel-block', Carousel);
customElements.define('carousel-block-items', CarouselItems);
customElements.define('carousel-block-item', CarouselItem);
customElements.define('carousel-prev-arrow', CarouselPrev);
customElements.define('carousel-next-arrow', CarouselNext);